import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Plus, Loader, RefreshCw, Search } from 'lucide-react';
import TradingWalletCard from './TradingWalletCard';
import { getUserTradingWallets, deleteTradingWallet, TradingWallet, restoreTradingWalletKeypair } from '../../services/tradingWalletService';
import InstantWalletTransfer from './InstantWalletTransfer';
import { notifyTradingWalletDeleted } from '../WalletConnection';
import { wsService } from '../../services/websocketService';

// Custom scrollbar styles
const scrollbarStyles = `
  /* Horizontal scrollbar styling */
  .horizontal-scroll::-webkit-scrollbar {
    height: 6px;
  }
  
  .horizontal-scroll::-webkit-scrollbar-track {
    background: rgba(30, 40, 40, 0.2);
    border-radius: 3px;
  }
  
  .horizontal-scroll::-webkit-scrollbar-thumb {
    background-color: rgba(71, 190, 143, 0.3);
    border-radius: 3px;
    transition: all 0.2s;
  }
  
  .horizontal-scroll::-webkit-scrollbar-thumb:hover {
    background-color: rgba(71, 190, 143, 0.5);
  }
  
  /* Draggable cursor styling */
  .draggable {
    cursor: grab;
    user-select: none;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
  }
  
  .draggable:active {
    cursor: grabbing;
  }
  
  /* Disable text selection during drag in Firefox */
  .draggable * {
    user-select: none;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
  }
  
  /* Drag indicator */
  .drag-indicator {
    position: absolute;
    top: -25px;
    left: 0;
    right: 0;
    text-align: center;
    font-size: 10px;
    color: rgba(255, 255, 255, 0.5);
    letter-spacing: 0.5px;
    opacity: 0;
    transform: translateY(10px);
    transition: all 0.3s ease;
    pointer-events: none;
    text-transform: uppercase;
  }
  
  .horizontal-scroll-container:hover .drag-indicator {
    opacity: 0.7;
    transform: translateY(0);
  }
  
  /* Fixed spacing to prevent outline clipping */
  .horizontal-scroll-container {
    padding-top: 3px;
    margin-top: 3px;
    padding-bottom: 3px;
    padding-left: 3px;
    padding-right: 3px;
    overflow: visible !important;
  }
  
  .card-wrapper {
    padding-top: 3px;
    padding-bottom: 3px;
    margin: 0;
  }
`;

interface TradingWalletListProps {
  walletAddress?: string;
  onCreateWallet: () => void;
  refreshTrigger?: number; // Optional prop to trigger refresh from parent
  onSelectWallet?: (wallet: TradingWallet | null) => void; // New prop for wallet selection
  onTransferClick?: (walletPublicKey: string) => void; // New prop for transfer button clicks
  onManageClick?: (walletPublicKey: string) => void; // New prop for manage button clicks
  walletTransactions?: any[]; // Transactions data for selected wallet
  loadingTransactions?: boolean; // Whether transactions are loading
  selectedWalletPublicKey?: string; // Public key of the selected wallet
  activeWalletPublicKey?: string; // Public key of the active trading wallet
  onTransactionRefresh?: (walletPublicKey: string) => void; // Callback to refresh transaction data
}

const TradingWalletList: React.FC<TradingWalletListProps> = ({ 
  walletAddress,
  onCreateWallet,
  refreshTrigger = 0,
  onSelectWallet,
  onTransferClick,
  onManageClick,
  walletTransactions = [],
  loadingTransactions = false,
  selectedWalletPublicKey,
  activeWalletPublicKey,
  onTransactionRefresh
}) => {
  const [wallets, setWallets] = useState<TradingWallet[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  // For the transfer modal
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [selectedWalletId, setSelectedWalletId] = useState<string | null>(null);
  
  // Track the currently selected wallet for transactions
  const [selectedWalletForTx, setSelectedWalletForTx] = useState<TradingWallet | null>(null);
  
  // Drag scrolling state
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  // Add state to track mouse movement for distinguishing drags from clicks
  const [dragDistance, setDragDistance] = useState(0);
  const [lastMouseX, setLastMouseX] = useState(0);
  
  // This function only loads the wallet list metadata, not the balances
  const loadWallets = async () => {
    if (!walletAddress) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const tradingWallets = await getUserTradingWallets(walletAddress);
      setWallets(tradingWallets);
      
      // If we had a selected wallet, find its updated version
      if (selectedWalletForTx) {
        const updatedSelectedWallet = tradingWallets.find(
          wallet => wallet.public_key === selectedWalletForTx.public_key
        );
        
        if (updatedSelectedWallet) {
          setSelectedWalletForTx(updatedSelectedWallet);
          if (onSelectWallet) onSelectWallet(updatedSelectedWallet);
        }
      }
    } catch (err) {
      console.error('Error loading trading wallets:', err);
      setError('Failed to load trading wallets');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (selectedWalletForTx) {
      // When a wallet is selected, immediately refresh its balances
      
      // Dispatch an event to trigger balance refresh for this wallet
      window.dispatchEvent(new CustomEvent('ekko:wallet_selected', {
        detail: {
          walletPublicKey: selectedWalletForTx.public_key
        }
      }));
    }
  }, [selectedWalletForTx]);

  // Add listener for transfer completion events
  useEffect(() => {
    const handleTransferCompletion = (event: CustomEvent<{ 
      fromWallet: string, 
      toWallet: string, 
      amount: number, 
      tokenMint: string, 
      transferType: string 
    }>) => {
      console.log('🔄 Transfer completed, refreshing trading wallet list...');
      
      const { fromWallet, toWallet } = event.detail;
      
      // Check if any of our wallets are involved
      const isAnyWalletInvolved = wallets.some(w => 
        w.public_key === fromWallet || w.public_key === toWallet
      );
      
      if (isAnyWalletInvolved) {
        console.log('🔄 Trading wallet involved in transfer, forcing refresh...');
        
        // Force immediate refresh of all trading wallets
        loadWallets();
        
        // Also refresh balances for affected wallets
        if (fromWallet) {
          window.dispatchEvent(new CustomEvent('ekko:wallet_selected', {
            detail: { walletPublicKey: fromWallet }
          }));
        }
        
        if (toWallet && toWallet !== fromWallet && !toWallet.includes('external')) {
          window.dispatchEvent(new CustomEvent('ekko:wallet_selected', {
            detail: { walletPublicKey: toWallet }
          }));
        }
      }
    };
    
    window.addEventListener('ekko:transfer_completed', handleTransferCompletion as EventListener);
    
    return () => {
      window.removeEventListener('ekko:transfer_completed', handleTransferCompletion as EventListener);
    };
  }, [wallets, loadWallets]);

  // Handle selecting a wallet for transaction history display
  const handleWalletSelect = async (wallet: TradingWallet) => {
    try {
      // If same wallet is clicked again (or is currently selected from parent), toggle selection off
      if ((selectedWalletForTx && selectedWalletForTx.public_key === wallet.public_key) ||
          (selectedWalletPublicKey && selectedWalletPublicKey === wallet.public_key)) {
        setSelectedWalletForTx(null);
        if (onSelectWallet) onSelectWallet(null);
        // Remove the keypair from websocket service
        wsService.removeActiveKeypair(wallet.public_key);
      } else {
        if (!walletAddress) {
          throw new Error('No wallet address available');
        }
        
        // Restore the keypair
        const keypair = await restoreTradingWalletKeypair(walletAddress, wallet.public_key);
        if (!keypair) {
          throw new Error('Failed to restore trading wallet keypair');
        }
        
        // Set the keypair in websocket service
        wsService.setActiveKeypair(wallet.public_key, keypair);
        
        setSelectedWalletForTx(wallet);
        if (onSelectWallet) onSelectWallet(wallet);
      }
    } catch (error) {
      console.error('Error selecting trading wallet:', error);
      setError('Failed to select trading wallet. Please try again.');
    }
  };

  // Handle card click for selection while preventing interference with other controls
  const handleCardClick = (e: React.MouseEvent, wallet: TradingWallet) => {
    // If we've dragged more than a small threshold, don't trigger selection
    if (dragDistance > 5) {
      return;
    }
    
    // Only handle selection if we're clicking the card itself, not controls inside it
    if ((e.target as HTMLElement).closest('.card-action-button')) {
      return; // Don't select if clicking an action button
    }
    
    handleWalletSelect(wallet);
  };
  
  // Update local selection state when selectedWalletPublicKey changes
  useEffect(() => {
    if (selectedWalletPublicKey && (!selectedWalletForTx || selectedWalletForTx.public_key !== selectedWalletPublicKey)) {
      // Find the wallet to select
      const walletToSelect = wallets.find(w => w.public_key === selectedWalletPublicKey);
      if (walletToSelect) {
        setSelectedWalletForTx(walletToSelect);
      }
    } else if (!selectedWalletPublicKey && selectedWalletForTx) {
      setSelectedWalletForTx(null);
    }
  }, [selectedWalletPublicKey, wallets]);
  
  // Drag-to-scroll handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    if (!scrollContainerRef.current) return;
    // Prevent default browser text selection
    e.preventDefault();
    setIsDragging(true);
    setStartX(e.pageX - scrollContainerRef.current.offsetLeft);
    setScrollLeft(scrollContainerRef.current.scrollLeft);
    setLastMouseX(e.pageX);
    setDragDistance(0);
    
    // Add temporary event listener to prevent text selection globally
    document.body.style.userSelect = 'none';
  };
  
  const handleMouseUp = (e: React.MouseEvent) => {
    setIsDragging(false);
    // Restore text selection when drag ends
    document.body.style.userSelect = '';
  };
  
  const handleMouseLeave = (e: React.MouseEvent) => {
    if (isDragging) {
      setIsDragging(false);
      // Restore text selection when drag ends
      document.body.style.userSelect = '';
    }
  };
  
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !scrollContainerRef.current) return;
    e.preventDefault();
    const x = e.pageX - scrollContainerRef.current.offsetLeft;
    const walk = (x - startX) * 1.5; // Speed multiplier
    scrollContainerRef.current.scrollLeft = scrollLeft - walk;
    
    // Track the distance moved to distinguish between drag and click
    const currentDistance = Math.abs(e.pageX - lastMouseX);
    setDragDistance(dragDistance + currentDistance);
    setLastMouseX(e.pageX);
  };
  
  // Touch event handlers for mobile devices
  const handleTouchStart = (e: React.TouchEvent) => {
    if (!scrollContainerRef.current || e.touches.length !== 1) return;
    // Don't prevent default here to allow normal touch interactions
    // Just track that we're starting a drag
    setIsDragging(true);
    setStartX(e.touches[0].clientX - scrollContainerRef.current.offsetLeft);
    setScrollLeft(scrollContainerRef.current.scrollLeft);
    setLastMouseX(e.touches[0].clientX);
    setDragDistance(0);
    
    // Add temporary event listener to prevent text selection globally
    document.body.style.userSelect = 'none';
  };
  
  const handleTouchEnd = (e: React.TouchEvent) => {
    setIsDragging(false);
    // Restore text selection when drag ends
    document.body.style.userSelect = '';
  };
  
  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging || !scrollContainerRef.current || e.touches.length !== 1) return;
    
    // Only prevent default if we're in a horizontal scroll container
    // This allows vertical scrolling to still work normally elsewhere
    if (Math.abs(e.touches[0].clientX - startX) > 10) {
      e.preventDefault();
    }
    
    const x = e.touches[0].clientX - scrollContainerRef.current.offsetLeft;
    const walk = (x - startX) * 1.5; // Speed multiplier
    scrollContainerRef.current.scrollLeft = scrollLeft - walk;
    
    // Track the distance moved to distinguish between drag and click
    const currentDistance = Math.abs(e.touches[0].clientX - lastMouseX);
    setDragDistance(dragDistance + currentDistance);
    setLastMouseX(e.touches[0].clientX);
  };
  
  useEffect(() => {
    if (walletAddress) {
      loadWallets();
    }
  }, [walletAddress, refreshTrigger]);
  
  const handleDeleteWallet = async (publicKey: string) => {
    if (!walletAddress) return;
    
    try {
      await deleteTradingWallet(walletAddress, publicKey);
      // If we deleted the selected wallet, clear the selection
      if (selectedWalletForTx && selectedWalletForTx.public_key === publicKey) {
        setSelectedWalletForTx(null);
        if (onSelectWallet) onSelectWallet(null);
      }
      // Refresh the list
      loadWallets();
      
      // Notify wallet connection component to update dropdown
      notifyTradingWalletDeleted();
    } catch (err) {
      console.error('Error deleting trading wallet:', err);
      setError('Failed to delete trading wallet');
    }
  };
  
  // Handle transfer success but don't force reload - rely on balance update events
  const handleTransferSuccess = () => {
    setShowTransferModal(false);
    
    // Trigger a balance refresh event for the selected wallet
    if (selectedWalletId) {
      window.dispatchEvent(new CustomEvent('ekko:wallet_selected', {
        detail: {
          walletPublicKey: selectedWalletId
        }
      }));
    }
  };

  const handleUpdateWallet = (updatedWallet: TradingWallet) => {
    // Update the wallet in the local wallets array
    setWallets(currentWallets => 
      currentWallets.map(wallet => 
        wallet.public_key === updatedWallet.public_key ? updatedWallet : wallet
      )
    );
    
    // If this was the selected wallet, update the selected wallet too
    if (selectedWalletForTx && selectedWalletForTx.public_key === updatedWallet.public_key) {
      setSelectedWalletForTx(updatedWallet);
      if (onSelectWallet) onSelectWallet(updatedWallet);
    }
  };

  const filteredWallets = wallets
    .filter(wallet => 
      wallet.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      wallet.public_key.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .sort((a, b) => {
      // Default sort by creation date (newest first)
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });
  
  if (!walletAddress) {
    return (
      <div className="content-card silver-border text-center py-8 px-4">
        <p className="text-gray-400 mb-4">Connect your wallet to view trading wallets</p>
      </div>
    );
  }
  
  return (
    <div className="wallet-list">
      {error && (
        <div className="mb-4 p-3 rounded-lg backdrop-blur-sm bg-red-500/10 border border-red-500/20 text-red-400 flex items-center justify-between">
          <p>{error}</p>
          <button 
            onClick={loadWallets} 
            className="px-2 py-1 text-[10px] rounded bg-red-500/20 hover:bg-red-500/30 transition-colors duration-200"
          >
            Retry
          </button>
        </div>
      )}
      
      <div className="flex justify-between mb-5">
        <div className="relative w-full max-w-xs">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search size={16} className="text-gray-400/80" />
          </div>
          <input 
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search wallets..."
            className="rounded-md backdrop-blur-sm bg-dark-grey/40 border border-gray-700/40 text-white/90 pl-9 w-full focus:outline-none focus:border-yellow-500/50 focus:ring-1 focus:ring-yellow-500/30 transition-all duration-200 h-9"
          />
        </div>
        
        <div className="flex gap-2">
          <button 
            onClick={loadWallets}
            className="py-1.5 px-3 rounded-md text-sm flex items-center gap-1.5 backdrop-blur-sm bg-[#47be8f]/80 text-black font-medium hover:bg-[#47be8f] transition-all duration-200"
            disabled={isLoading}
          >
            <RefreshCw size={14} className={isLoading ? "animate-spin" : ""} />
            {isLoading ? "Loading..." : "Refresh Wallets"}
          </button>
        </div>
      </div>
      
      {isLoading && wallets.length === 0 ? (
        <div className="flex items-center justify-center py-8">
          <Loader className="w-6 h-6 text-yellow-500 animate-spin" />
        </div>
      ) : filteredWallets.length === 0 ? (
        <div className="p-8 rounded-lg backdrop-blur-sm bg-dark-grey/40 border border-gray-700/40 text-center">
          <p className="text-gray-400 mb-4">No trading wallets found</p>
          <button 
            onClick={onCreateWallet}
            className="py-1.5 px-3 rounded-md text-sm flex items-center gap-1.5 mx-auto backdrop-blur-sm bg-yellow-500/80 text-black font-medium hover:bg-yellow-500 transition-all duration-200"
          >
            <Plus size={14} />
            Create Wallet
          </button>
        </div>
      ) : (
        <div className="relative">
          <style>{scrollbarStyles}</style>
          
          <div className="relative horizontal-scroll-container">
            <div className="drag-indicator">drag to scroll →</div>
            <div 
              ref={scrollContainerRef}
              className="overflow-x-auto horizontal-scroll py-4 px-4 rounded-lg draggable"
              onMouseDown={handleMouseDown}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseLeave}
              onMouseMove={handleMouseMove}
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
              onTouchMove={handleTouchMove}
            >
              <div className="flex space-x-6 min-w-max py-2">
                {filteredWallets.map((wallet) => (
                  <div 
                    className={`w-[280px] flex-shrink-0 transition-transform duration-200 card-wrapper ${
                      selectedWalletForTx && selectedWalletForTx.public_key === wallet.public_key 
                        ? 'transform scale-[1.02]' 
                        : ''
                    }`} 
                    key={wallet.public_key}
                    onClick={(e) => handleCardClick(e, wallet)}
                  >
                    <TradingWalletCard 
                      wallet={wallet}
                      onDelete={handleDeleteWallet}
                      onUpdate={handleUpdateWallet}
                      className="h-full transition-all cursor-pointer"
                      allWallets={wallets}
                      userWalletAddress={walletAddress || ''}
                      isSelected={selectedWalletForTx?.public_key === wallet.public_key || selectedWalletPublicKey === wallet.public_key}
                      onTransferClick={onTransferClick}
                      onManageClick={onManageClick}
                      isActive={wallet.public_key === activeWalletPublicKey}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
          
        </div>
      )}
      
      {/* Global transfer modal */}
      {showTransferModal && selectedWalletId && (
        <InstantWalletTransfer
          wallets={wallets}
          userWalletAddress={walletAddress || ''}
          sourceWalletId={selectedWalletId}
          onClose={() => setShowTransferModal(false)}
          onSuccess={handleTransferSuccess}
          onWalletUpdate={handleUpdateWallet}
        />
      )}
    </div>
  );
};

export default TradingWalletList;
